import React, { useEffect, useState } from "react";
import { Copy, Link2, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PLATFORMS = [["instagram", "Instagram"], ["tiktok", "TikTok"], ["facebook", "Facebook"], ["youtube", "YouTube"], ["whatsapp", "WhatsApp"], ["email", "Email"]];
const card = "mt-6 rounded-2xl border border-border bg-card p-4";
const head = "font-mono text-[11px] uppercase tracking-widest text-primary";
const input = "rounded-full border border-border bg-background px-4 py-2 text-sm focus:border-primary focus:outline-none";
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

// Admin: which sources turn visitors into buyers (first place they came from vs the last link they clicked).
export const ConversionTable = ({ days }) => {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/admin/conversion?days=${days}`).then((r) => setData(r.data)).catch(() => setData({ rows: [], buyers: 0 })); }, [days]);
  return (
    <div className={card} data-testid="conversion-table">
      <p className={head}>Visitors to buyers, by source</p>
      <p className="mt-1 text-xs text-muted-foreground">"First came from" credits where a buyer first found you; "Last click" credits the last link they used before buying.</p>
      {!data ? <p className="mt-3 text-sm">Loading…</p> : data.rows.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No visitors yet.</p> : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[30rem] text-sm">
            <thead><tr className="text-left font-mono text-[11px] uppercase tracking-widest text-muted-foreground"><th className="py-2 pr-3">Source</th><th className="pr-3">Visitors</th><th className="pr-3">Buyers (first came from)</th><th>Buyers (last click)</th></tr></thead>
            <tbody>{data.rows.map((r) => (
              <tr key={r.label} className="border-t border-border" data-testid="conversion-row">
                <td className="py-2 pr-3 font-semibold">{r.label}</td><td className="pr-3">{r.visitors}</td>
                <td className="pr-3">{r.buyers_first} <span className="text-muted-foreground">({pct(r.share_first)})</span></td>
                <td>{r.buyers_last} <span className="text-muted-foreground">({pct(r.share_last)})</span></td>
              </tr>
            ))}</tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground" data-testid="conversion-buyers">{data.buyers} purchase{data.buyers === 1 ? "" : "s"} in this period.</p>
        </div>
      )}
    </div>
  );
};

const LinkRow = ({ ln, onDelete }) => {
  const copy = () => navigator.clipboard.writeText(ln.url).then(() => toast.success("Link copied")).catch(() => toast.error("Couldn't copy — select the link and copy it"));
  return (
    <li className="border-t border-border py-3 text-sm" data-testid="track-link-row">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">{ln.name} <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{ln.platform_label}</span></p>
        <p className="text-xs text-muted-foreground" data-testid="track-link-stats"><b className="text-foreground">{ln.visitors}</b> visitors · <b className="text-foreground">{ln.buyers_first}</b> buyers first came from it · <b className="text-foreground">{ln.buyers_last}</b> bought after clicking it last</p>
      </div>
      <div className="mt-1 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-background px-2 py-1 text-xs" data-testid="track-link-url">{ln.url}</code>
        <button type="button" onClick={copy} data-testid="track-link-copy" className="inline-flex items-center gap-1 rounded-full border border-primary px-3 py-1 text-xs font-semibold text-primary"><Copy size={12} /> Copy</button>
        <button type="button" onClick={() => onDelete(ln)} data-testid="track-link-delete" aria-label={`Delete ${ln.name}`} className="rounded-full p-1.5 text-muted-foreground hover:text-red-500"><Trash2 size={14} /></button>
      </div>
    </li>
  );
};

// Admin: make a tagged site link for each social post and see the visitors and buyers it brings.
export const TrackLinks = () => {
  const [links, setLinks] = useState(null);
  const [form, setForm] = useState({ name: "", platform: "instagram", path: "/" });
  const load = () => api.get("/admin/track-links").then((r) => setLinks(r.data.links)).catch(() => setLinks([]));
  useEffect(() => { load(); }, []);
  const create = (e) => {
    e.preventDefault();
    const path = `/${form.path.trim().replace(/^\/+/, "")}`;
    api.post("/admin/track-links", { ...form, path }).then(() => { toast.success("Link created — copy it into your post"); setForm({ ...form, name: "" }); load(); })
      .catch((err) => toast.error(err.response?.status === 422 ? "Check the post name, and that the page is like /lessons (letters, numbers, - or / only)" : err.response?.data?.detail || "Couldn't create the link"));
  };
  const remove = (ln) => { if (window.confirm(`Delete the link "${ln.name}"? Its visits stay in your totals.`)) api.delete(`/admin/track-links/${ln.id}`).then(load); };
  return (
    <div className={card} data-testid="track-links">
      <p className={`flex items-center gap-2 ${head}`}><Link2 size={13} /> Post links</p>
      <p className="mt-1 text-xs text-muted-foreground">Make one link per post, paste it into the post or bio, and see which posts bring visitors and buyers.</p>
      <form onSubmit={create} className="mt-3 flex flex-wrap gap-2">
        <input required minLength={2} maxLength={60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Post name, e.g. Insta reel Nov 3" data-testid="track-link-name" className={`${input} min-w-[14rem] flex-1`} />
        <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} data-testid="track-link-platform" className={input}>
          {PLATFORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} placeholder="/" data-testid="track-link-path" className={`${input} w-40`} title="Page the link opens, e.g. / or /lessons" />
        <button type="submit" data-testid="track-link-create" className="rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground">Make link</button>
      </form>
      {links === null ? <p className="mt-3 text-sm">Loading…</p> : links.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No post links yet.</p> : (
        <ul className="mt-3">{links.map((ln) => <LinkRow key={ln.id} ln={ln} onDelete={remove} />)}</ul>
      )}
    </div>
  );
};

// Admin: preview of the Monday 9am visitor email.
export const WeeklyEmailPreview = () => {
  const [mail, setMail] = useState(null);
  const open = () => (mail ? setMail(null) : api.get("/admin/weekly-visitors-preview").then((r) => setMail(r.data)).catch(() => toast.error("Couldn't load the preview")));
  return (
    <div className={card} data-testid="weekly-email">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={`flex items-center gap-2 ${head}`}><Mail size={13} /> Monday visitor email</p>
        <button type="button" onClick={open} data-testid="weekly-email-preview" className="rounded-full border border-primary px-4 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10">{mail ? "Hide preview" : "Preview this Monday's email"}</button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Every Monday at 9am (UK time) you get last week's visitors, top sources, post links and purchases.</p>
      {mail && (
        <div className="mt-3 rounded-xl border border-border bg-white p-2 text-black">
          <p className="px-2 py-1 text-sm font-semibold" data-testid="weekly-email-subject">{mail.subject}</p>
          <iframe title="Weekly visitor email preview" srcDoc={mail.html} sandbox="" className="h-96 w-full" data-testid="weekly-email-frame" />
        </div>
      )}
    </div>
  );
};
